import { Info } from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { ForecastTimeline } from "@/components/forecast/ForecastTimeline";
import { Card, CardContent } from "@/components/ui/card";
import {
	Tooltip as UiTooltip,
	TooltipContent as UiTooltipContent,
	TooltipProvider as UiTooltipProvider,
	TooltipTrigger as UiTooltipTrigger,
} from "@/components/ui/tooltip";
import { useApp } from "@/contexts/AppContext";
import { useUserPreferenceDefaults } from "@/contexts/UserPreferenceDefaultsContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useFormatting } from "@/hooks/useFormatting";
import { useScenarioForecast } from "@/hooks/useScenarioForecast";
import { useTerminology } from "@/hooks/useTerminology";
import {
	applyScenarioToForecast,
	formatCoverageValue,
} from "@/lib/scenarioEngine";
import type { ScenarioChange } from "@/types/scenarios";

interface ScenarioImpactPreviewProps {
	// The scenario ID — used to pre-seed the ForecastTimeline overlay
	scenarioId?: string;
	// All adjustments from all groups (builder internal format — used for confidence score)
	adjustments: {
		revenue: any[];
		cost: any[];
	};
	// Live preview data (from forms being edited)
	livePreview?: {
		revenue: any | null;
		cost: any | null;
	};
	// Actual ScenarioChange[] from the scenario — used by the engine for accurate metrics
	scenarioChanges?: ScenarioChange[];
	// Base forecast data (from current state) — kept for fallback
	baseForecast?: {
		currentCash: number;
		runway: number;
		minCash: number;
		minCashDate: string;
	};
	// Render mode: 'chart-only' renders just the chart; 'sidebar' renders risk/confidence/summary only; default renders all
	mode?: "chart-only" | "sidebar" | "full";
	// Drill-down "View Transactions" handler for scenario overlay points. Passed
	// straight through to the embedded ForecastTimeline so the host page can
	// filter its scenario transactions table to the selected day.
	onViewTransactions?: (date: Date) => void;
}

// Custom tooltip matching the main ForecastTimeline style
const ScenarioChartTooltip = ({
	active,
	payload,
}: {
	active?: boolean;
	payload?: any[];
}) => {
	const { formatCurrency } = useFormatting();
	if (!active || !payload || payload.length === 0) return null;
	const data = payload[0]?.payload;
	if (!data) return null;
	return (
		<div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
			<p className="font-semibold mb-2 text-neutral-700">{data.date}</p>
			{payload.map((entry: any) => (
				<div key={entry.dataKey} className="flex items-center gap-2">
					<div
						className="w-2 h-2 rounded-full"
						style={{ backgroundColor: entry.color }}
					/>
					<span className="text-neutral-600">{entry.name}:</span>
					<span className="font-medium">
						{formatCurrency(
							typeof entry.value === "number" ? entry.value : 0,
							"full",
						)}
					</span>
				</div>
			))}
		</div>
	);
};

export const ScenarioImpactPreview: React.FC<ScenarioImpactPreviewProps> = ({
	scenarioId,
	adjustments,
	livePreview = {
		revenue: null,
		cost: null,
	},
	scenarioChanges,
	baseForecast: baseForecastProp,
	mode = "full",
	onViewTransactions,
}) => {
	const { formatDate, formatCurrency } = useFormatting();
	const { currentUser, mode: appMode } = useApp();
	const { getUserPreferences } = useUserPreferences();
	const { defaults } = useUserPreferenceDefaults();
	const userPrefs = getUserPreferences(currentUser.id);

	// In live mode, fetch server forecast metrics for authoritative Net Impact
	const { data: serverForecast } = useScenarioForecast(scenarioId ?? 0, "90d");
	const serverMetrics = serverForecast?.metrics ?? null;

	// ── Engine-driven calculations ──────────────────────────────────────────────
	// When scenarioChanges are provided, use the real engine for accurate metrics.
	// Falls back to the simplified burn rate formula if not provided (e.g. new scenario).
	const {
		baseForecast: baseForecastData,
		isLoading: baseForecastLoading,
		forecastNotReady,
	} = useBaseForecast();

	// Derive base forecast metrics from context data when prop is not explicitly provided
	const derivedBaseForecast = useMemo((): {
		currentCash: number;
		runway: number;
		minCash: number;
		minCashDate: string;
	} | null => {
		if (baseForecastData.length === 0) return null;
		const first = baseForecastData[0];
		let minBal = first?.balance ?? 0;
		let minDate: Date | string = first?.date ?? new Date();
		let totalIn = 0;
		let totalOut = 0;
		for (const p of baseForecastData) {
			if (p.balance < minBal) {
				minBal = p.balance;
				minDate = p.date;
			}
			totalIn += p.incoming;
			totalOut += p.outgoing;
		}
		const avgDailyBurn = (totalOut - totalIn) / baseForecastData.length;
		const runway =
			avgDailyBurn > 0 ? (first?.balance ?? 0) / avgDailyBurn / 30.44 : 18;
		return {
			currentCash: first?.balance ?? 0,
			runway: Math.round(runway * 10) / 10,
			minCash: Math.round(minBal),
			minCashDate:
				minDate instanceof Date
					? minDate.toISOString().split("T")[0]
					: String(minDate),
		};
	}, [baseForecastData]);

	// Use prop if explicitly passed, otherwise use derived values from context
	const baseForecast = baseForecastProp ?? derivedBaseForecast;

	const engineResult = useMemo(() => {
		if (!scenarioChanges || scenarioChanges.length === 0) return null;
		return applyScenarioToForecast(baseForecastData, scenarioChanges);
	}, [scenarioChanges, baseForecastData]);

	const engineMetrics = engineResult?.metrics ?? null;
	const engineForecast = engineResult?.projectedForecast ?? null;
	const { cashDurationShortTerm } = useTerminology();
	const metricLabel =
		cashDurationShortTerm.charAt(0).toUpperCase() +
		cashDurationShortTerm.slice(1);
	const metricUnit = cashDurationShortTerm === "cash until" ? "date" : "months";

	// Calculate total adjustments count
	const totalAdjustments = adjustments.revenue.length + adjustments.cost.length;

	// Calculate net impact from all adjustments + live preview
	const calculateNetImpact = () => {
		let netImpact = 0;

		// Revenue adjustments (positive impact)
		adjustments.revenue.forEach((adj) => {
			if (adj.type === "adjust-revenue-percent") {
				netImpact += adj.monthlyImpact || 0;
			} else if (adj.type === "change-revenue") {
				// Canonical type for adjust-revenue-percent
				netImpact += adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "add-scenario-transaction" ||
				adj.type === "add-revenue"
			) {
				// Form type or canonical type for adding a revenue transaction
				netImpact += adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-revenue"
			) {
				// Form type or canonical type for adjusting a specific transaction
				netImpact += (adj.valueChange ?? adj.details?.valueChange ?? 0) / 12;
			}
		});

		// Add live revenue preview (if form is being edited)
		if (livePreview.revenue) {
			const adj = livePreview.revenue;
			if (adj.type === "adjust-revenue-percent") {
				netImpact += adj.monthlyImpact || 0;
			} else if (adj.type === "change-revenue") {
				netImpact += adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "add-scenario-transaction" ||
				adj.type === "add-revenue"
			) {
				netImpact += adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-revenue"
			) {
				netImpact += (adj.valueChange ?? adj.details?.valueChange ?? 0) / 12;
			}
		}

		// Cost adjustments (savings = positive, increases = negative)
		adjustments.cost.forEach((adj) => {
			if (adj.type === "adjust-costs-percent") {
				netImpact += adj.monthlyImpact || 0; // Negative for increases, positive for reductions
			} else if (adj.type === "change-costs") {
				// Canonical type for adjust-costs-percent
				const monthlySavings = adj.details?.monthlySavings ?? 0;
				const monthlyIncrease = adj.details?.monthlyIncrease ?? 0;
				if (adj.details?.changeType === "reduction") {
					netImpact += Number(monthlySavings);
				} else {
					netImpact -= Number(monthlyIncrease);
				}
			} else if (
				adj.type === "add-scenario-transaction" ||
				adj.type === "add-cost"
			) {
				// Form type or canonical type for adding a cost transaction
				netImpact -= adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-cost"
			) {
				// Form type or canonical type for adjusting a specific cost transaction
				netImpact += (adj.valueChange ?? adj.details?.valueChange ?? 0) / 12;
			}
		});

		// Add live cost preview
		if (livePreview.cost) {
			const adj = livePreview.cost;
			if (adj.type === "adjust-costs-percent") {
				netImpact += adj.monthlyImpact || 0;
			} else if (adj.type === "change-costs") {
				const monthlySavings = adj.details?.monthlySavings ?? 0;
				const monthlyIncrease = adj.details?.monthlyIncrease ?? 0;
				if (adj.details?.changeType === "reduction") {
					netImpact += Number(monthlySavings);
				} else {
					netImpact -= Number(monthlyIncrease);
				}
			} else if (
				adj.type === "add-scenario-transaction" ||
				adj.type === "add-cost"
			) {
				netImpact -= adj.details?.monthlyAmount ?? (adj.amount || 0) / 12;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-cost"
			) {
				netImpact += (adj.valueChange ?? adj.details?.valueChange ?? 0) / 12;
			}
		}

		return netImpact;
	};

	const formNetImpact = calculateNetImpact();

	// Priority: server forecast metrics (live mode, authoritative) → local engine → form calculation
	const netImpact =
		appMode === "live" && serverMetrics
			? serverMetrics.netImpact
			: engineMetrics
				? engineMetrics.netImpact
				: formNetImpact;

	// Monthly is net impact divided by 3 (90-day window = ~3 months)
	const monthlyImpact =
		appMode === "live" && serverMetrics
			? serverMetrics.netImpact / 3
			: engineMetrics
				? engineMetrics.netImpact / 3
				: formNetImpact;

	// Helper function: Round to nearest £1,000
	const roundToThousand = (value: number): number => {
		return Math.round(value / 1000) * 1000;
	};

	// Helper function: Round to 1 decimal place
	const roundToOneDecimal = (value: number): number => {
		return Math.round(value * 10) / 10;
	};

	// ── Base forecast unavailable guard ──────────────────────────────────────────
	// When no base forecast data exists (live mode, no forecast run), render an
	// empty state instead of calculations that depend on base metrics.
	if (!baseForecast) {
		if (baseForecastLoading) {
			return (
				<Card>
					<CardContent className="py-8 text-center">
						<p className="text-sm text-muted-foreground">
							Loading base forecast…
						</p>
					</CardContent>
				</Card>
			);
		}
		return (
			<Card>
				<CardContent className="py-8 text-center">
					<p className="text-sm text-muted-foreground">
						{forecastNotReady
							? "Run a base forecast first to see impact analysis"
							: "Base forecast not available"}
					</p>
				</CardContent>
			</Card>
		);
	}

	// ── Engine-driven metrics (when scenarioChanges provided) ──────────────────
	// These replace the simplified burn rate formula for accuracy.
	// Falls back to simplified formula for new scenarios with no saved changes.

	const scenarioCash = engineMetrics
		? engineMetrics.endingCash
		: roundToThousand(baseForecast.currentCash + monthlyImpact * 3);

	const baseBurnRate = baseForecast.currentCash / baseForecast.runway;
	const newBurnRate = baseBurnRate - monthlyImpact;

	let scenarioRunway: number;
	let runwayChange: number;
	let isPositiveCashFlow = false;

	if (engineMetrics) {
		scenarioRunway = engineMetrics.runway;
		isPositiveCashFlow = !isFinite(scenarioRunway);
		runwayChange = isPositiveCashFlow
			? Infinity
			: roundToOneDecimal(scenarioRunway - baseForecast.runway);
	} else if (newBurnRate <= 0) {
		scenarioRunway = Infinity;
		isPositiveCashFlow = true;
		runwayChange = Infinity;
	} else {
		scenarioRunway = roundToOneDecimal(baseForecast.currentCash / newBurnRate);
		runwayChange = roundToOneDecimal(scenarioRunway - baseForecast.runway);
	}

	const scenarioMinCash = engineMetrics
		? engineMetrics.minCash
		: roundToThousand(Math.max(0, baseForecast.minCash + monthlyImpact * 2));

	const cashChange = roundToThousand(scenarioCash - baseForecast.currentCash);
	const minCashChange = roundToThousand(scenarioMinCash - baseForecast.minCash);

	// Calculate confidence score
	const calculateConfidence = () => {
		if (totalAdjustments === 0) return 100;

		let totalWeight = 0;
		let weightedSum = 0;

		// Cost adjustments = 85% (likely)
		adjustments.cost.forEach((adj) => {
			if (adj.type === "adjust-costs-percent" || adj.type === "change-costs") {
				// Percentage adjustments are more certain
				weightedSum += 90;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-cost"
			) {
				// Transaction adjustments are very certain
				weightedSum += 95;
			} else {
				// Scenario transactions (add-scenario-transaction / add-cost) depend on confidence level
				const confidence =
					adj.confidence || adj.details?.confidence || "probable";
				if (confidence === "certain") weightedSum += 95;
				else if (confidence === "probable") weightedSum += 75;
				else if (confidence === "possible") weightedSum += 50;
				else weightedSum += 25; // unlikely
			}
			totalWeight += 1;
		});

		// Revenue adjustments = 70-90% (depends on type)
		adjustments.revenue.forEach((adj) => {
			if (
				adj.type === "adjust-revenue-percent" ||
				adj.type === "change-revenue"
			) {
				// Percentage adjustments are fairly certain
				weightedSum += 85;
			} else if (
				adj.type === "adjust-transaction" ||
				adj.type === "adjust-revenue"
			) {
				// Transaction adjustments are very certain
				weightedSum += 95;
			} else {
				// Scenario transactions (add-scenario-transaction / add-revenue) depend on confidence level
				const confidence =
					adj.confidence || adj.details?.confidence || "probable";
				if (confidence === "certain") weightedSum += 95;
				else if (confidence === "probable") weightedSum += 75;
				else if (confidence === "possible") weightedSum += 50;
				else weightedSum += 25; // unlikely
			}
			totalWeight += 1;
		});

		return totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 100;
	};

	const confidence = calculateConfidence();

	// Generate chart data (90 days)
	// Uses real engine projection when available, falls back to burn rate formula

	// Chart card — rendered in chart-only and full mode
	// The real ForecastTimeline chart — pre-seeded with this scenario as an overlay.
	// This is the exact same component used on the 90-day forecast page.
	const chartCard = scenarioId ? (
		<ForecastTimeline
			initialScenarioIds={[scenarioId]}
			hideMetricCards
			enableViewScenario={false}
			onViewTransactions={onViewTransactions}
		/>
	) : null;

	// Chart-only mode — just the chart, no header or sidebar sections
	if (mode === "chart-only") {
		return chartCard;
	}

	return (
		<UiTooltipProvider>
			<div
				className={
					mode === "sidebar"
						? "grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch"
						: "space-y-4"
				}
			>
				{/* Header — full mode only */}
				{mode === "full" && (
					<div className="flex items-center gap-2 mb-4">
						<div
							className="w-1 h-6 rounded-full"
							style={{ backgroundColor: "var(--accent-teal-500)" }}
						/>
						<h3 className="text-lg font-semibold text-gray-900">
							Impact Summary
						</h3>
					</div>
				)}

				{/* Chart — full mode only */}
				{mode === "full" && chartCard}

				{/* Min Cash Card */}
				<Card>
					<CardContent className="pt-3 pb-3 px-4">
						<div className="space-y-1.5">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-1">
									<span className="text-xs text-gray-600">Min Cash</span>
									<UiTooltip>
										<UiTooltipTrigger asChild>
											<Info className="w-3 h-3 text-gray-400 cursor-help" />
										</UiTooltipTrigger>
										<UiTooltipContent side="top" className="max-w-xs">
											<p className="font-medium mb-1">Minimum Cash Position</p>
											<p className="text-xs text-gray-600">
												The lowest cash balance reached at any point during the
												90-day scenario period — this is your worst-case cash
												floor under this scenario.
											</p>
											<p className="text-xs text-gray-600 mt-1">
												Calculation: The lowest projected daily balance across
												the full scenario period, derived from the base forecast
												adjusted by all scenario changes.
											</p>
										</UiTooltipContent>
									</UiTooltip>
								</div>
								<span className="text-xs font-semibold text-gray-900">
									{formatCurrency(scenarioMinCash, "full")}
								</span>
							</div>
							<div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
								<div className="flex items-center gap-1">
									<span className="text-xs text-gray-600">vs Base</span>
									<UiTooltip>
										<UiTooltipTrigger asChild>
											<Info className="w-3 h-3 text-gray-400 cursor-help" />
										</UiTooltipTrigger>
										<UiTooltipContent side="top" className="max-w-xs">
											<p className="text-xs text-gray-600">
												The difference between this scenario's minimum cash and
												the base forecast's minimum cash. Positive means this
												scenario improves your worst-case position; negative
												means it worsens it.
											</p>
										</UiTooltipContent>
									</UiTooltip>
								</div>
								<span
									className={`text-xs font-semibold ${minCashChange >= 0 ? "[color:var(--accent-success-500)]" : "text-amber-600"}`}
								>
									{minCashChange >= 0 ? "+" : ""}
									{formatCurrency(minCashChange, "full")}
								</span>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Confidence Score */}
				<Card>
					<CardContent className="pt-3 pb-3 px-4 h-full flex flex-col justify-between">
						<div className="space-y-1.5">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-1">
									<span className="text-xs text-gray-600">Confidence</span>
									<UiTooltip>
										<UiTooltipTrigger asChild>
											<Info className="w-3 h-3 text-gray-400 cursor-help" />
										</UiTooltipTrigger>
										<UiTooltipContent side="top" className="max-w-xs">
											<p className="font-medium mb-1">
												Weighted Confidence Score
											</p>
											<p className="text-xs text-gray-600">
												A weighted score across all adjustments — not a simple
												average. Each adjustment type carries a base confidence
												weight, then modified by the certainty level you set on
												it.
											</p>
											<p className="text-xs text-gray-600 mt-1 space-y-0.5">
												<span className="block">• Certain → 95%</span>
												<span className="block">• Probable → 75%</span>
												<span className="block">• Possible → 50%</span>
												<span className="block">• Unlikely → 25%</span>
												<span className="block">
													• Cost/% adjustments → 90–95% (high certainty by
													nature)
												</span>
											</p>
										</UiTooltipContent>
									</UiTooltip>
								</div>
								<span className="text-xs font-semibold text-gray-900">
									{confidence}%
								</span>
							</div>
							<div className="w-full bg-gray-200 rounded-full h-1 mb-1">
								<div
									className={`h-1 rounded-full ${confidence >= 80 ? "[background:var(--accent-success-500)]" : confidence >= 60 ? "bg-amber-500" : "bg-gray-400"}`}
									style={{ width: `${confidence}%` }}
								/>
							</div>
							<div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
								<span className="text-xs text-gray-600">Rating</span>
								<span className="text-xs font-semibold text-gray-900">
									{confidence >= 80
										? "High"
										: confidence >= 60
											? "Medium"
											: "Low"}
								</span>
							</div>
							<div className="flex items-center justify-between">
								<span className="text-xs text-gray-600">Based on</span>
								<span className="text-xs font-semibold text-gray-900">
									{totalAdjustments} adjustment
									{totalAdjustments !== 1 ? "s" : ""}
								</span>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Quick Summary */}
				<Card>
					<CardContent className="pt-3 pb-3 px-4">
						<div className="space-y-1.5">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-1">
									<span className="text-xs text-gray-600">Adjustments</span>
									<UiTooltip>
										<UiTooltipTrigger asChild>
											<Info className="w-3 h-3 text-gray-400 cursor-help" />
										</UiTooltipTrigger>
										<UiTooltipContent side="top" className="max-w-xs">
											<p className="text-xs text-gray-600">
												Total number of individual adjustments applied in this
												scenario across all groups (revenue, cost, headcount
												etc.).
											</p>
										</UiTooltipContent>
									</UiTooltip>
								</div>
								<span className="text-xs font-semibold text-gray-900">
									{totalAdjustments}
								</span>
							</div>
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-1">
									<span className="text-xs text-gray-600">Net Impact</span>
									<UiTooltip>
										<UiTooltipTrigger asChild>
											<Info className="w-3 h-3 text-gray-400 cursor-help" />
										</UiTooltipTrigger>
										<UiTooltipContent side="top" className="max-w-xs">
											<p className="font-medium mb-1">Total Net Cash Impact</p>
											<p className="text-xs text-gray-600">
												The total cash difference this scenario creates over the
												full 90-day period vs the base forecast.
											</p>
											<p className="text-xs text-gray-600 mt-1">
												Calculation: Sum of all revenue adjustments minus sum of
												all cost adjustments across the scenario period.
											</p>
										</UiTooltipContent>
									</UiTooltip>
								</div>
								<span
									className={`text-xs font-semibold ${netImpact >= 0 ? "[color:var(--accent-success-500)]" : "text-amber-600"}`}
								>
									{netImpact >= 0 ? "+" : ""}
									{formatCurrency(netImpact, "full")}
								</span>
							</div>
							<div className="flex items-center justify-between pt-1.5 border-t border-gray-100">
								<div className="flex items-center gap-1">
									<span className="text-xs text-gray-600">Monthly avg</span>
									<UiTooltip>
										<UiTooltipTrigger asChild>
											<Info className="w-3 h-3 text-gray-400 cursor-help" />
										</UiTooltipTrigger>
										<UiTooltipContent side="top" className="max-w-xs">
											<p className="text-xs text-gray-600">
												The average monthly cash impact of this scenario.
											</p>
											<p className="text-xs text-gray-600 mt-1">
												Calculation: Total net impact ÷ 3 months (the 90-day
												window).
											</p>
										</UiTooltipContent>
									</UiTooltip>
								</div>
								<span
									className={`text-xs font-semibold ${netImpact >= 0 ? "[color:var(--accent-success-500)]" : "text-amber-600"}`}
								>
									{netImpact >= 0 ? "+" : ""}
									{formatCurrency(monthlyImpact, "full")}/mo
								</span>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		</UiTooltipProvider>
	);
};
